import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  TemplateRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal,
  computed,
  effect,
  Injector
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, startWith, map, debounceTime, distinctUntilChanged, takeUntil, combineLatest } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { AssignService } from '../../Modules/assign/assign.service';
import { CustomTableViewComponent, TableColumn } from '../../common/custom-table-view/custom-table-view.component';
import { EditRequestComponent } from '../../Dialogs/Dashboards/Requests/edit-request/edit-request.component';
import { MatDialog } from '@angular/material/dialog';
import { CommentComponent } from '../../Dialogs/Dashboards/Requests/comment/comment.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, ChartConfiguration, ChartOptions, registerables } from 'chart.js';
import { AngularMaterialModule } from '../../angular-material.module';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  AdminDashboardService,
  DashboardFilters,
  ChartDatum,
  StatusItem,
  ProjectItem,
  PriorityItem,
  CategoryItem,
  AssigneeItem
} from './admin-dashboard.service';
import { BreadcrumbComponent } from '../../common/breadcrumb/breadcrumb/breadcrumb.component';

// Register Chart.js components once
Chart.register(...registerables);

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    AngularMaterialModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    NgSelectModule,
    NgxMatSelectSearchModule,
    BaseChartDirective,
    BreadcrumbComponent,
    CustomTableViewComponent
  ],
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('priorityCell') priorityCell!: TemplateRef<any>;
  @ViewChild('descriptionCell') descriptionCell!: TemplateRef<any>;
  @ViewChild('approvedCell') approvedCell!: TemplateRef<any>;
  @ViewChild('commentsActions') commentsActions!: TemplateRef<any>;

  // Session storage values (immutable after init)
  private readonly accountID = sessionStorage.getItem('account_id');
  private readonly userID = sessionStorage.getItem('session_id');
  private readonly roleID = sessionStorage.getItem('role_id');

  // Destroy subject for subscription management
  private readonly destroy$ = new Subject<void>();

  // Signals for reactive state management
  readonly filtersApplied = signal<boolean>(false);
  readonly loadingData = signal<boolean>(false);
  readonly loading = signal<boolean>(false);
  submitted = false;

  // Card counts as signals
  readonly totalRequests = signal<number>(0);
  readonly openRequests = signal<number>(0);
  readonly closedRequests = signal<number>(0);
  readonly pendingRequests = signal<number>(0);

  // Chart data as signals
  readonly requestTypeData = signal<ChartDatum[]>([]);
  readonly assigneeData = signal<ChartDatum[]>([]);
  readonly priorityData = signal<ChartDatum[]>([]);
  readonly statusData = signal<ChartDatum[]>([]);
  readonly projectPhaseData = signal<ChartDatum[]>([]);
  readonly requestAgeData = signal<ChartDatum[]>([]);

  // Dropdown lists as signals
  readonly projectList = signal<ProjectItem[]>([]);
  readonly statusList = signal<StatusItem[]>([]);
  readonly categoryList = signal<CategoryItem[]>([]);
  readonly priorityList = signal<PriorityItem[]>([]);
  readonly assigneeList = signal<AssigneeItem[]>([]);

  // Computed values for chart totals (memoized)
  readonly requestTypeTotal = computed(() =>
    this.requestTypeData().reduce((acc, item) => acc + item.value, 0)
  );
  readonly assigneeTotal = computed(() =>
    this.assigneeData().reduce((acc, item) => acc + item.value, 0)
  );
  readonly priorityTotal = computed(() =>
    this.priorityData().reduce((acc, item) => acc + item.value, 0)
  );
  readonly statusTotal = computed(() =>
    this.statusData().reduce((acc, item) => acc + item.value, 0)
  );
  readonly projectPhaseTotal = computed(() =>
    this.projectPhaseData().reduce((acc, item) => acc + item.value, 0)
  );
  readonly requestAgeTotal = computed(() =>
    this.requestAgeData().reduce((acc, item) => acc + item.value, 0)
  );

  // Form controls
  projectCtrlSearch = new FormControl<string>('');
  statusSearchCtrl = new FormControl<string>('');
  categorySearchCtrl = new FormControl<string>('');
  prioritySearchCtrl = new FormControl<string>('');
  assigneeSearchCtrl = new FormControl<string>('');

  // Reactive form group
  filterForm: FormGroup;

  // Table
  tableData: any[] = [];
  tableColumns: TableColumn[] = [];

  // Filtered observables for autocomplete (memoized)
  filteredProjectList$!: Observable<ProjectItem[]>;
  filteredStatusList$!: Observable<StatusItem[]>;
  filteredCategoryList$!: Observable<CategoryItem[]>;
  filteredPriorityList$!: Observable<PriorityItem[]>;
  filteredAssigneeList$!: Observable<AssigneeItem[]>;

  // Chart configurations (immutable after initialization)
  readonly barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6c757d' }
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { color: 'rgba(0,0,0,0.05)' }
      }
    }
  };

  readonly pieOptions: ChartOptions<'polarArea'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right' },
      tooltip: { enabled: true }
    }
  };

  // Chart data objects (mutable references for Chart.js)
  requestTypeChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: '#0d6efd' }]
  };

  assigneeChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: '#198754' }]
  };

  priorityChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: '#fd7e14' }]
  };

  statusChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: '#6f42c1' }]
  };

  statusPieData: ChartConfiguration<'polarArea'>['data'] = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [
        '#0d6efd', '#198754', '#fd7e14',
        '#6f42c1', '#20c997', '#ffc107'
      ]
    }]
  };

  constructor(
    private readonly dashboardService: AdminDashboardService,
    private readonly assignService: AssignService,
    private readonly snackBar: MatSnackBar,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector
  ) {
    // Initialize form with custom validator
    this.filterForm = this.fb.group({
      project_id: [null, [this.projectRequiredValidator]],
      status: [null],
      category: [null],
      priority: [null],
      assignee: [null]
    });

  }

  ngOnInit(): void {
    // Setup filtered observables first (they will update when data loads)
    this.setupFilteredObservables();
    // Then load the data
    this.loadProjects();
    this.loadDropdowns();
  }

  ngAfterViewInit(): void {
    this.tableColumns = [
      { key: 'sr_no', title: '#', sortable: false, width: 60, filterable: false },
      { key: 'account_name', title: 'Project Name' },
      { key: 'created_by_name', title: 'Created By' },
      { key: 'status_name', title: 'Status', format: (v) => v || '-' },
      { key: 'category_name', title: 'Category', format: (v) => v || '-' },
      { key: 'priority_name', title: 'Priority', sortable: false, cellTemplate: this.priorityCell },
      { key: 'description', title: 'Description', sortable: false, cellTemplate: this.descriptionCell },
      { key: 'assigned_to_name', title: 'Assigned To', format: (v) => v || '-' },
      { key: 'assigned_by_name', title: 'Assigned By', format: (v) => v || '-' },
      { key: 'approved_by_name', title: 'Approved By', format: (v) => v || '-' },
      { key: 'approved', title: 'Approved', sortable: false, cellTemplate: this.approvedCell },
      { key: 'created_at', title: 'Created At', format: (v) => this.formatTableDate(v) },
      { key: 'updated_at', title: 'Updated At', format: (v) => this.formatTableDate(v) }
    ];
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup filtered observables for autocomplete with debouncing
   * Uses combineLatest to react to both search input and underlying list changes
   */
  private setupFilteredObservables(): void {
    // Project filter - reactive to both search input and project list signal
    this.filteredProjectList$ = combineLatest([
      this.projectCtrlSearch.valueChanges.pipe(startWith(''), debounceTime(200), distinctUntilChanged()),
      toObservable(this.projectList, { injector: this.injector })
    ]).pipe(
      map(([searchValue]) => this.filterProjectList(searchValue || ''))
    );

    // Status filter - reactive to both search input and status list signal
    this.filteredStatusList$ = combineLatest([
      this.statusSearchCtrl.valueChanges.pipe(startWith(''), debounceTime(200), distinctUntilChanged()),
      toObservable(this.statusList, { injector: this.injector })
    ]).pipe(
      map(([searchValue]) => this.filterStatusList(searchValue || ''))
    );

    // Category filter - reactive to both search input and category list signal
    this.filteredCategoryList$ = combineLatest([
      this.categorySearchCtrl.valueChanges.pipe(startWith(''), debounceTime(200), distinctUntilChanged()),
      toObservable(this.categoryList, { injector: this.injector })
    ]).pipe(
      map(([searchValue]) => this.filterCategoryList(searchValue || ''))
    );

    // Priority filter - reactive to both search input and priority list signal
    this.filteredPriorityList$ = combineLatest([
      this.prioritySearchCtrl.valueChanges.pipe(startWith(''), debounceTime(200), distinctUntilChanged()),
      toObservable(this.priorityList, { injector: this.injector })
    ]).pipe(
      map(([searchValue]) => this.filterPriorityList(searchValue || ''))
    );

    // Assignee filter - reactive to both search input and assignee list signal
    this.filteredAssigneeList$ = combineLatest([
      this.assigneeSearchCtrl.valueChanges.pipe(startWith(''), debounceTime(200), distinctUntilChanged()),
      toObservable(this.assigneeList, { injector: this.injector })
    ]).pipe(
      map(([searchValue]) => this.filterAssigneeList(searchValue || ''))
    );
  }

  /**
   * Load projects
   */
  loadProjects(): void {
    const payload = { user_id: parseInt(this.userID || '0') };
    this.assignService.fetchProjects(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success && res.data && res.data.length > 0) {
            this.projectList.set(res.data);
            if (!this.filtersApplied()) {
              const accountNum = this.accountID ? Number(this.accountID) : null;
              const matched = accountNum ? res.data.find((p: any) => p.account_id === accountNum) : null;
              const defaultSelected = matched ? [matched.account_id] : [res.data[0].account_id];
              this.filterForm.patchValue({ project_id: defaultSelected }, { emitEvent: false });
              this.applyFilters();
            }
          } else {
            this.projectList.set([]);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.projectList.set([]);
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Load all dropdowns in parallel
   */
  loadDropdowns(): void {
    this.dashboardService.fetchDropdowns(this.accountID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.statusList.set(data.status);
          this.priorityList.set(data.priority);
          this.categoryList.set(data.category);
          this.assigneeList.set(
            data.assignee.map((a) => ({
              ...a,
              display_name: `${a.first_name} ${a.last_name}`.trim(),
            }))
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open('Error loading dropdown data', 'Close', { duration: 3000 });
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Apply filters and load all dashboard data
   */
  applyFilters(): void {
    this.submitted = true;
    const projectIdValue = this.filterForm.get('project_id')?.value;
    if (!projectIdValue || (Array.isArray(projectIdValue) && projectIdValue.length === 0)) {
      this.snackBar.open('Please select at least one project', 'Close', { duration: 3000 });
      return;
    }

    this.loadingData.set(true);
    this.filtersApplied.set(true);

    const formValue = this.filterForm.value;
    const filters: DashboardFilters = {
      account_id: formValue.project_id?.length ? formValue.project_id : null,
      status: formValue.status?.length ? formValue.status : null,
      category: formValue.category?.length ? formValue.category : null,
      priority_id: formValue.priority?.length ? formValue.priority : null,
      assigned_to: formValue.assignee?.length ? formValue.assignee : null
    };

    // Fetch all data in parallel using the service
    this.dashboardService.fetchDashboardData(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          // Update card counts
          this.totalRequests.set(data.cardCounts.totalRequests);
          this.openRequests.set(data.cardCounts.openRequests);
          this.closedRequests.set(data.cardCounts.closedRequests);
          this.pendingRequests.set(data.cardCounts.pendingRequests);

          // Update chart data
          this.requestTypeData.set(data.requestTypeData);
          this.assigneeData.set(data.assigneeData);
          this.priorityData.set(data.priorityData);
          this.statusData.set(data.statusData);
          this.projectPhaseData.set(data.projectPhaseData);
          this.requestAgeData.set(data.requestAgeData);

          // Update charts
          this.updateAllCharts();

          // Update table with mapped data
          this.updateTableData(data.requests);

          this.loadingData.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open('Error loading dashboard data', 'Close', { duration: 3000 });
          this.loadingData.set(false);
          this.loading.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Update table data with proper mapping
   */
  private updateTableData(requests: any[]): void {
    this.loading.set(true);
    const statusList = this.statusList();
    const categoryList = this.categoryList();
    const priorityList = this.priorityList();
    const assigneeList = this.assigneeList();

    // Create lookup maps for O(1) access instead of O(n) find operations
    const statusMap = new Map(statusList.map(s => [s.status_id, s.status]));
    const categoryMap = new Map(categoryList.map(c => [c.issue_category_id, c.issue_category]));
    const priorityMap = new Map(priorityList.map(p => [p.priority_id, p.priority]));
    const assigneeMap = new Map(
      assigneeList.map(a => [a.user_id, `${a.first_name} ${a.last_name}`])
    );

    this.tableData = requests.map((d, index) => ({
      ...d,
      sr_no: index + 1,
      status_name: statusMap.get(d.status) || d.status_name || '-',
      category_name: categoryMap.get(d.issue_category_id) || d.category_name || '-',
      priority_name: priorityMap.get(d.priority_id) || d.priority_name || '-',
      assigned_to_name: d.assigned_to_name || assigneeMap.get(d.assigned_to) || '-'
    }));

    this.loading.set(false);
    this.cdr.markForCheck();
  }

  /**
   * Update all charts efficiently
   */
  private updateAllCharts(): void {
    this.updateBarChart(this.requestTypeData(), this.requestTypeChartData, '#0d6efd');
    this.updateBarChart(this.assigneeData(), this.assigneeChartData, '#198754');
    this.updateBarChart(this.priorityData(), this.priorityChartData, '#fd7e14');
    this.updateBarChart(this.statusData(), this.statusChartData, '#6f42c1');
    this.updatePieChart(this.statusData(), this.statusPieData);
  }

  /**
   * Update bar chart data
   */
  private updateBarChart(
    source: ChartDatum[],
    target: ChartConfiguration<'bar'>['data'],
    color: string
  ): void {
    target.labels = source.map(d => d.extra?.displayName ?? d.name);
    target.datasets = [{
      data: source.map(d => d.value),
      backgroundColor: color,
      hoverBackgroundColor: color
    }];
  }

  /**
   * Update pie chart data
   */
  private updatePieChart(
    source: ChartDatum[],
    target: ChartConfiguration<'polarArea'>['data']
  ): void {
    target.labels = source.map(d => d.extra?.displayName ?? d.name);
    target.datasets[0].data = source.map(d => d.value);
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filterForm.reset({
      project_id: null,
      status: null,
      category: null,
      priority: null,
      assignee: null
    });
    this.projectCtrlSearch.setValue('');
    this.statusSearchCtrl.setValue('');
    this.categorySearchCtrl.setValue('');
    this.prioritySearchCtrl.setValue('');
    this.assigneeSearchCtrl.setValue('');

    this.tableData = [];
    this.totalRequests.set(0);
    this.openRequests.set(0);
    this.closedRequests.set(0);
    this.pendingRequests.set(0);

    // Reset charts
    this.requestTypeData.set([]);
    this.priorityData.set([]);
    this.statusData.set([]);
    this.assigneeData.set([]);
    this.projectPhaseData.set([]);
    this.requestAgeData.set([]);

    this.updateAllCharts();
    this.filtersApplied.set(false);
    this.cdr.markForCheck();
  }

  /**
   * Open edit dialog
   */
  openEditDialog(item: any): void {
    const dialogRef = this.dialog.open(EditRequestComponent, {
      width: '706px',
      maxHeight: '90vh',
      data: { item, for: 'issue' },
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.applyFilters();
        }
      });
  }

  /**
   * Open comments dialog
   */
  openComments(issueId: number): void {
    this.dialog.open(CommentComponent, {
      width: '560px',
      maxHeight: '90vh',
      data: { issueId },
    });
  }

  /**
   * Project required validator
   */
  private projectRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return { required: true };
    }
    return null;
  };

  /**
   * Filter functions (optimized with lowercase caching)
   */
  private filterProjectList(searchValue: string): ProjectItem[] {
    const filterValue = searchValue.toLowerCase();
    return this.projectList().filter(option =>
      option.project_name.toLowerCase().includes(filterValue)
    );
  }

  private filterStatusList(searchValue: string): StatusItem[] {
    const filterValue = searchValue.toLowerCase();
    return this.statusList().filter(option =>
      option.status.toLowerCase().includes(filterValue)
    );
  }

  private filterCategoryList(searchValue: string): CategoryItem[] {
    const filterValue = searchValue.toLowerCase();
    return this.categoryList().filter(option =>
      option.issue_category.toLowerCase().includes(filterValue)
    );
  }

  private filterPriorityList(searchValue: string): PriorityItem[] {
    const filterValue = searchValue.toLowerCase();
    return this.priorityList().filter(option =>
      option.priority.toLowerCase().includes(filterValue)
    );
  }

  private filterAssigneeList(searchValue: string): AssigneeItem[] {
    const filterValue = searchValue.toLowerCase();
    return this.assigneeList().filter(option =>
      `${option.first_name} ${option.last_name}`.toLowerCase().includes(filterValue)
    );
  }

  /**
   * TrackBy functions for *ngFor loops (performance optimization)
   */
  trackByProjectId(index: number, item: ProjectItem): number {
    return item.account_id;
  }

  trackByStatusId(index: number, item: StatusItem): number {
    return item.status_id;
  }

  trackByCategoryId(index: number, item: CategoryItem): number {
    return item.issue_category_id;
  }

  trackByPriorityId(index: number, item: PriorityItem): number {
    return item.priority_id;
  }

  trackByAssigneeId(index: number, item: AssigneeItem): number {
    return item.user_id;
  }

  private formatTableDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  /**
   * Display functions for autocomplete
   */
  displayStatus = (s: StatusItem | null): string => s?.status || '';
  displayCategory = (c: CategoryItem | null): string => c?.issue_category || '';
  displayPriority = (p: PriorityItem | null): string => p?.priority || '';
  displayAssignee = (a: AssigneeItem | null): string =>
    a ? `${a.first_name} ${a.last_name}` : '';
  projectLabel = (project: ProjectItem | null): string => project?.project_name || '';
}
